import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../axios-instance";
import UpdateCardModal from "../subscription/update-card-modal";

/* ─── Design tokens ─────────────────────────────────────────────────────── */
const C = {
  navy:        "#0c4767",
  orange:      "#FE9920",
  orangeLight: "#FFF7ED",
  dark:        "#111827",
  mid:         "#374151",
  muted:       "#6B7280",
  faint:       "#9CA3AF",
  border:      "#E5E7EB",
  surface:     "#F9FAFB",
  white:       "#ffffff",
  green:       "#10B981",
  greenBg:     "#F0FDF4",
  greenBorder: "#BBF7D0",
  greenDark:   "#065F46",
  red:         "#EF4444",
  redBg:       "#FEF2F2",
};

/* ─── Card brand icon (inline SVG) ─────────────────────────────────────── */
const BrandIcon = ({ brand }) => {
  const b = (brand || "").toLowerCase();

  if (b === "visa") return (
    <svg width="48" height="30" viewBox="0 0 48 30" fill="none" aria-label="Visa">
      <rect width="48" height="30" rx="4" fill="#1A1F71"/>
      <text x="50%" y="20" textAnchor="middle" fill="#fff" fontSize="13" fontWeight="700" fontFamily="Arial, sans-serif" letterSpacing="1">VISA</text>
    </svg>
  );

  if (b === "mastercard") return (
    <svg width="48" height="30" viewBox="0 0 48 30" fill="none" aria-label="Mastercard">
      <rect width="48" height="30" rx="4" fill="#252525"/>
      <circle cx="19" cy="15" r="9" fill="#EB001B"/>
      <circle cx="29" cy="15" r="9" fill="#F79E1B"/>
      <path d="M24 8.2a9 9 0 0 1 0 13.6A9 9 0 0 1 24 8.2z" fill="#FF5F00"/>
    </svg>
  );

  if (b === "amex" || b === "american express") return (
    <svg width="48" height="30" viewBox="0 0 48 30" fill="none" aria-label="American Express">
      <rect width="48" height="30" rx="4" fill="#007BC1"/>
      <text x="50%" y="20" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700" fontFamily="Arial, sans-serif" letterSpacing="0.5">AMEX</text>
    </svg>
  );

  if (b === "discover") return (
    <svg width="48" height="30" viewBox="0 0 48 30" fill="none" aria-label="Discover">
      <rect width="48" height="30" rx="4" fill="#fff" stroke="#E5E7EB"/>
      <circle cx="30" cy="15" r="10" fill="#F76F20"/>
      <text x="9" y="19" fill="#231F20" fontSize="8" fontWeight="700" fontFamily="Arial, sans-serif">DISCOVER</text>
    </svg>
  );

  /* Generic card icon */
  return (
    <svg width="48" height="30" viewBox="0 0 48 30" fill="none" aria-label="Card">
      <rect width="48" height="30" rx="4" fill="#E5E7EB"/>
      <rect x="4" y="10" width="40" height="5" rx="1" fill="#9CA3AF"/>
      <rect x="4" y="20" width="12" height="3" rx="1" fill="#9CA3AF"/>
      <rect x="19" y="20" width="12" height="3" rx="1" fill="#9CA3AF"/>
    </svg>
  );
};

/* ─── Card display widget ───────────────────────────────────────────────── */
const CardDisplay = ({ brand, lastFour, onChangeClick }) => (
  <div style={{
    background: `linear-gradient(135deg, ${C.navy} 0%, #1a6b99 100%)`,
    borderRadius: "16px",
    padding: "28px 28px 24px",
    color: C.white,
    position: "relative",
    overflow: "hidden",
    maxWidth: "380px",
    boxShadow: "0 8px 32px rgba(12,71,103,0.28)",
  }}>
    {/* Decorative circles */}
    <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.07)" }} />
    <div style={{ position: "absolute", bottom: -40, right: 20, width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />

    {/* Brand icon */}
    <div style={{ marginBottom: "24px" }}>
      <BrandIcon brand={brand} />
    </div>

    {/* Masked number */}
    <div style={{ fontSize: "20px", fontWeight: 600, letterSpacing: "3px", fontFamily: "'Courier New', monospace", marginBottom: "20px" }}>
      •••• •••• •••• {lastFour}
    </div>

    {/* Footer row */}
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <p style={{ margin: 0, fontSize: "10px", opacity: 0.6, textTransform: "uppercase", letterSpacing: "1px" }}>Card brand</p>
        <p style={{ margin: 0, fontSize: "14px", fontWeight: 600 }}>{brand || "Card"}</p>
      </div>
      <button
        type="button"
        onClick={onChangeClick}
        style={{
          background: "rgba(255,255,255,0.15)",
          border: "1px solid rgba(255,255,255,0.3)",
          color: C.white,
          borderRadius: "8px",
          padding: "7px 16px",
          fontSize: "13px",
          fontWeight: 600,
          cursor: "pointer",
          backdropFilter: "blur(4px)",
          transition: "background 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.25)"}
        onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.15)"}
      >
        Change card
      </button>
    </div>
  </div>
);

/* ─── Empty state ───────────────────────────────────────────────────────── */
const NoCardState = ({ onAddClick }) => (
  <div style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 32px",
    background: C.surface,
    borderRadius: "16px",
    border: `2px dashed ${C.border}`,
    maxWidth: "380px",
    textAlign: "center",
  }}>
    {/* Icon */}
    <div style={{ width: 60, height: 60, borderRadius: "50%", background: C.orangeLight, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.orange} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
        <line x1="1" y1="10" x2="23" y2="10"/>
      </svg>
    </div>
    <p style={{ margin: "0 0 6px", fontSize: "15px", fontWeight: 700, color: C.dark }}>No payment method on file</p>
    <p style={{ margin: "0 0 24px", fontSize: "13px", color: C.muted, lineHeight: 1.6 }}>
      Add a card to manage your subscription and pay for services.
    </p>
    <button
      type="button"
      onClick={onAddClick}
      style={{
        background: C.orange,
        color: C.white,
        border: "none",
        borderRadius: "10px",
        padding: "11px 28px",
        fontSize: "14px",
        fontWeight: 700,
        cursor: "pointer",
        transition: "background 0.15s",
        boxShadow: "0 2px 8px rgba(254,153,32,0.35)",
      }}
      onMouseEnter={e => e.currentTarget.style.background = "#e8880e"}
      onMouseLeave={e => e.currentTarget.style.background = C.orange}
    >
      + Add payment method
    </button>
  </div>
);

/* ─── Main CardInfo component ───────────────────────────────────────────── */
const CardInfo = () => {
  const [cardBrand, setCardBrand]       = useState(null);
  const [cardLastFour, setCardLastFour] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [showModal, setShowModal]       = useState(false);

  const fetchCard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/user-details");
      const data = res?.data?.data || res?.data || {};
      setCardBrand(data.card_brand || null);
      setCardLastFour(data.card_last_four || null);
    } catch {
      /* silent — card fields stay null */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCard(); }, [fetchCard]);

  /* After UpdateCardModal closes, re-fetch to reflect new card */
  const handleModalToggle = (open) => {
    setShowModal(open);
    if (!open) fetchCard();
  };

  const hasCard = !!(cardBrand || cardLastFour);

  return (
    <div style={{ padding: "32px 28px", maxWidth: "700px" }}>

      {/* Section header */}
      <div style={{ marginBottom: "28px" }}>
        <h2 style={{ margin: "0 0 6px", fontSize: "18px", fontWeight: 700, color: C.dark }}>
          Payment Method
        </h2>
        <p style={{ margin: 0, fontSize: "13px", color: C.muted, lineHeight: 1.6 }}>
          The card on file is used for your subscription renewals and any paid services.
          All transactions are secured by Stripe.
        </p>
      </div>

      {/* Card area */}
      {loading ? (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "24px",
          background: C.surface,
          borderRadius: "16px",
          maxWidth: "380px",
          border: `1px solid ${C.border}`,
        }}>
          {/* Skeleton shimmer */}
          <div style={{ width: 48, height: 30, borderRadius: 4, background: "#E5E7EB" }} />
          <div style={{ flex: 1 }}>
            <div style={{ height: 12, borderRadius: 6, background: "#E5E7EB", marginBottom: 8, width: "70%" }} />
            <div style={{ height: 10, borderRadius: 6, background: "#E5E7EB", width: "40%" }} />
          </div>
        </div>
      ) : hasCard ? (
        <CardDisplay
          brand={cardBrand}
          lastFour={cardLastFour}
          onChangeClick={() => setShowModal(true)}
        />
      ) : (
        <NoCardState onAddClick={() => setShowModal(true)} />
      )}

      {/* Security note */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "7px",
        marginTop: "24px",
        fontSize: "12px",
        color: C.faint,
      }}>
        <svg width="13" height="13" viewBox="0 0 20 20" fill={C.faint} aria-hidden="true">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
        </svg>
        Card details are encrypted and secured by Stripe. We never store your full card number.
      </div>

      {/* Update Card Modal (Stripe Elements) */}
      {showModal && <UpdateCardModal toggle={handleModalToggle} />}
    </div>
  );
};

export default CardInfo;
